import { Autocomplete, TextField } from '@mui/material'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { useDebounce } from 'use-debounce'

import { Case, CaseStatus, Maybe, OrderCaseBy, UserType, useFetchAllMattersWithoutJoinLazyQuery } from '../../../generated'
import { GRAPHQL_QUERY_POLICY_CACHE_FIRST, PAGE_LIMIT } from '../../constants'
import { MatterDropdownWithFormProps } from '../../interfaceTypes/props'

const MatterDropdownWithForm = ({
  name,
  label = 'Select Matter',
  required = false,
  disabled = false,
  placeholder,
  helperText,
  error,
  userId,
  setRate = false,
  seedCaseNumber = false,
  autoSelectOnTab = false,
  customOnChange,
  initialCase
}: MatterDropdownWithFormProps) => {
  const { control, setValue, watch } = useFormContext()

  const [cases, setCases] = useState<Maybe<Case>[]>([])
  const [casePage, setCasePage] = useState<number>(1)
  const [hasMorePages, setHasMorePages] = useState<boolean>(true)
  const [searchValue, setSearchValue] = useState<string>('')
  const [selectedCase, setSelectedCase] = useState<Maybe<Case> | null>(null)
  const [debouncedSearchValue] = useDebounce(searchValue, 500)
  const [open, setOpen] = useState(false)

  // Subscribed, not read once: the field can be cleared from outside (clearing
  // the User re-scopes the matter list, so the old matter is dropped), and the
  // *displayed* selection has to follow. `watch(name)` in the effect body with
  // the value missing from the deps meant this never re-ran on that change, so
  // the input kept showing a matter the form no longer held.
  const caseId = useWatch({ name, control })

  useEffect(() => {
    if (!caseId) {
      setSelectedCase(null)
      return
    }
    if (selectedCase?.id === caseId) return

    const found = (cases || []).find(c => c?.id === caseId) ?? null
    if (found) {
      setSelectedCase(found)
    } else if (initialCase && initialCase.id === caseId) {
      setSelectedCase(initialCase as Maybe<Case>)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, cases, initialCase])

  const [fetchCases, { loading }] = useFetchAllMattersWithoutJoinLazyQuery({
    ...GRAPHQL_QUERY_POLICY_CACHE_FIRST,
    variables: undefined,
    onError() {
      setCases([])
      setHasMorePages(false)
    },
    onCompleted(data) {
      const { cases: newCases, pagination, response } = data?.fetchAllMattersWithoutJoin ?? {}

      // The query resolves with a status even when it fails, so a failure lands
      // here rather than in onError. Drop the whole list in that case: leaving
      // the previously loaded pages up would keep offering matters that the
      // failed request was meant to replace.
      if ((response?.status ?? 200) >= 400) {
        setCases([])
        setHasMorePages(false)
        return
      }

      // No matters for this query. On the first page that means the list is
      // genuinely empty — clear it instead of keeping stale options. On a later
      // page it only means we have run out, so keep what is already loaded.
      if (!newCases?.length) {
        if (casePage === 1) setCases([])
        setHasMorePages(false)
        return
      }

      setCases(prevCases =>
        casePage === 1 ? (newCases as Maybe<Case>[]) : [...(prevCases ?? []), ...(newCases as Maybe<Case>[])]
      )
      const { page, totalPages } = pagination || {}
      setHasMorePages((page || 0) < (totalPages || 0))
    }
  })

  // Restart the list whenever *what we are listing* changes. `userId` belongs
  // here as much as the search term: matters are scoped to the user, so the
  // pages already loaded no longer belong to a newly selected one. Leaving it
  // out meant the previous user's matters were merged into the new user's list
  // — and worse, a user with no matters left `hasMorePages` false, which made
  // the fetch below bail out for every user picked afterwards.
  useEffect(() => {
    if (open) {
      setCases([])
      setCasePage(1)
      setHasMorePages(true)
    }
  }, [debouncedSearchValue, userId, open])

  // The reset above lands one render *after* the user/search changes, so in the
  // commit between the two this effect would fire a request for the previous
  // page number of a list it is about to restart — a wasted round trip whose
  // response is then discarded. Remembering what was last requested lets us
  // skip that in-between commit and wait for page 1.
  const requestKey = `${userId ?? ''}|${debouncedSearchValue.trim()}`
  const requestKeyRef = useRef(requestKey)

  useEffect(() => {
    if (!open || !hasMorePages) return
    if (requestKeyRef.current !== requestKey && casePage !== 1) return
    requestKeyRef.current = requestKey

    const preselectedCase = selectedCase
    const preselectedSearch =
      casePage === 1 && preselectedCase
        ? (preselectedCase.isConsultMatter ? preselectedCase.consultMatterNumber : preselectedCase.caseNumber) ?? ''
        : ''
    const searchTerm = debouncedSearchValue.trim() || preselectedSearch

    fetchCases({
      variables: {
        casesInput: {
          orderBy: OrderCaseBy.Title,
          userId: userId ?? undefined,
          status: CaseStatus.Active,
          searchValue: searchTerm,
          isConsultCase: undefined,
          paginationOptions: {
            limit: PAGE_LIMIT,
            page: casePage
          }
        }
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMorePages, casePage, debouncedSearchValue, fetchCases, userId, open, requestKey])

  const options = useMemo(() => {
    return cases || []
  }, [cases])

  const handleScroll = useCallback(
    (event: React.SyntheticEvent) => {
      const listboxNode = event.currentTarget
      if (
        listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 5 &&
        hasMorePages &&
        !loading
      ) {
        setCasePage(prev => prev + 1)
      }
    },
    [hasMorePages, loading]
  )

  const handleInputChange = useCallback((_event: React.SyntheticEvent, newInputValue: string, reason: string) => {
    if (reason === 'input') {
      setSelectedCase(null)
      setSearchValue(newInputValue)
    }

    if (reason === 'clear') {
      setSelectedCase(null)
      setSearchValue('')
    }
  }, [])

  const handleChange = useCallback(
    (_event: React.SyntheticEvent, newValue: Maybe<Case> | null, reason: string) => {
      if (newValue) {
        setSelectedCase(newValue)
        customOnChange?.({ label: newValue.title || '', value: newValue.id })
        if (seedCaseNumber) {
          // Seed the matter number so downstream rate lookups can search for
          // THIS case (paginated matter lists may not contain it otherwise).
          setValue('caseNumber', newValue.isConsultMatter ? newValue.consultMatterNumber : newValue.caseNumber)
        }
        if (setRate) {
          const userId = watch('userId')
          // Match CommonEntryFields.computeMatterRate: a user may have several
          // rows on the matter; take the highest rate across their non-creator
          // roles (don't exclude originatorAttorney).
          const rates = (newValue.caseToUsers ?? [])
            .filter(caseToUser => caseToUser?.userId === userId && caseToUser?.userType !== UserType.Creator)
            .map(caseToUser => caseToUser?.hourlyRate ?? 0)
          setValue('rate', rates.length ? Math.max(...rates) : 0)
        }
      } else if (reason === 'clear') {
        setSelectedCase(null)
        setSearchValue('')
      }

      return newValue?.id ?? null
    },
    [customOnChange, setRate, seedCaseNumber, setValue, watch]
  )

  const getOptionLabel = (option: Maybe<Case>) => {
    if (!option) return ''
    const caseNumber = option.isConsultMatter ? option.consultMatterNumber : option?.caseNumber
    const title = option?.title || ''
    if (caseNumber) {
      return `(#${caseNumber}) ${title}`
    }
    return title
  }
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error: fieldError } }) => {
        return (
          <Autocomplete
            {...field}
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            options={options}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            onChange={(event, newValue, reason) => {
              const processedValue = handleChange(event, newValue, reason)
              field.onChange(processedValue || null)

              if (reason === 'clear' && typeof customOnChange === 'function') customOnChange(null)
            }}
            onInputChange={handleInputChange}
            inputValue={selectedCase ? getOptionLabel(selectedCase) : searchValue}
            value={selectedCase ?? field.value}
            disabled={disabled}
            filterOptions={x => x}
            // Only auto-highlight (and therefore auto-select on blur/tab) once the user has
            // actually typed a search query. With nothing typed, no option is highlighted, so
            // blurring/tabbing out of an untouched dropdown commits nothing instead of defaulting
            // to the first matter.
            autoHighlight={autoSelectOnTab && searchValue.trim().length > 0}
            autoSelect={autoSelectOnTab && searchValue.trim().length > 0}
            renderInput={params => (
              <TextField
                {...params}
                label={label}
                placeholder={placeholder}
                required={required}
                error={!!error || !!fieldError}
                helperText={fieldError?.message || helperText}
                sx={{
                  '& .MuiInputBase-input': {
                    fontSize: '0.8125rem'
                  }
                }}
              />
            )}
            ListboxProps={{
              role: 'list-box',
              style: {
                maxHeight: '190px'
              },
              onScroll: handleScroll
            }}
            sx={{ width: '100%' }}
            noOptionsText={loading ? 'Loading...' : cases?.length === 0 ? 'No Matters' : 'No options'}
            loading={loading}
            selectOnFocus
          />
        )
      }}
    />
  )
}

export default memo(MatterDropdownWithForm)
